import { z } from 'zod';

import { VestigasHTTPError } from '../VestigasHTTPError';

import { ConnectionCheckResponseError } from './parseConnectionCheck';
import { ConnectionCheckTimeoutError } from './runConnectionCheck';
import { type ConnectionCheck, type ConnectionCheckResult } from './types';

/**
 * What a check means for the person looking at it. `unavailable` is a check
 * that could not run, which says nothing about the connection itself.
 */
export type ConnectionCheckOutcome =
  | {
      readonly kind: 'failed' | 'notCheckable' | 'ok' | 'warning';
      readonly result: ConnectionCheckResult;
    }
  | {
      readonly kind: 'unavailable';
      /**
       * `checkFailed`: the check broke on the API's side. `invalidRequest`:
       * the form built a request our own schema refuses.
       */
      readonly reason: 'checkFailed' | 'error' | 'invalidRequest' | 'timeout';
    }
  | {
      /** The API's own explanation, when it sent one. */
      readonly detail?: string;
      readonly kind: 'unavailable';
      readonly reason: 'rateLimited';
    }
  | {
      /** Why the API refused to start the check. */
      readonly detail: string;
      readonly kind: 'unavailable';
      readonly reason: 'rejected';
    };

export function outcomeFromCheck(
  check: ConnectionCheck,
): ConnectionCheckOutcome {
  const { result } = check;

  if (check.state !== 'finished' || !result) {
    return { kind: 'unavailable', reason: 'checkFailed' };
  }

  if (result.status === 'not_checkable') {
    return { kind: 'notCheckable', result };
  }

  if (result.status !== 'ok') {
    return { kind: 'failed', result };
  }

  return { kind: result.warnings.length > 0 ? 'warning' : 'ok', result };
}

/** The API's explanation of a refused request, when it sent a readable one. */
function toErrorDetail({ parsedBody }: VestigasHTTPError) {
  if (typeof parsedBody === 'string') {
    return parsedBody.trim() || undefined;
  }

  if (parsedBody && Array.isArray(parsedBody.detail)) {
    const messages = parsedBody.detail.flatMap((item: unknown) =>
      item &&
      typeof item === 'object' &&
      'msg' in item &&
      typeof item.msg === 'string'
        ? [item.msg]
        : [],
    );

    return messages.length > 0 ? messages.join('; ') : undefined;
  }

  return undefined;
}

export function outcomeFromError(error: unknown): ConnectionCheckOutcome {
  if (error instanceof ConnectionCheckTimeoutError) {
    return { kind: 'unavailable', reason: 'timeout' };
  }

  if (error instanceof z.ZodError) {
    return { kind: 'unavailable', reason: 'invalidRequest' };
  }

  if (!(error instanceof VestigasHTTPError)) {
    return { kind: 'unavailable', reason: 'error' };
  }

  const { status, statusText } = error.response;
  const detail = toErrorDetail(error);

  if (status === 429) {
    return { detail, kind: 'unavailable', reason: 'rateLimited' };
  }

  // A 404 without an explanation is an endpoint that is not there, not an
  // answer about what was checked.
  if (status === 404 && detail === undefined) {
    return { kind: 'unavailable', reason: 'error' };
  }

  if (status >= 400 && status < 500) {
    return {
      detail: detail ?? `${status} ${statusText}`.trim(),
      kind: 'unavailable',
      reason: 'rejected',
    };
  }

  return { kind: 'unavailable', reason: 'error' };
}

/**
 * Whether an error ending a check is our own failure, worth reporting. A
 * response of another shape was already reported when it was parsed.
 */
export function isReportableConnectionCheckError(error: unknown) {
  if (error instanceof ConnectionCheckResponseError) {
    return false;
  }

  const outcome = outcomeFromError(error);

  return (
    outcome.kind === 'unavailable' &&
    (outcome.reason === 'error' || outcome.reason === 'invalidRequest')
  );
}

export type ConnectionCheckSaveDecision = 'block' | 'save' | 'saveUnchecked';

/**
 * Whether a save may go ahead after checking. A failure blocks only until it
 * was shown; a check that could not run never blocks.
 */
export function getSaveDecision(
  outcome: ConnectionCheckOutcome,
  { wasShownBeforeSaving }: { wasShownBeforeSaving: boolean },
): ConnectionCheckSaveDecision {
  switch (outcome.kind) {
    case 'failed': {
      return wasShownBeforeSaving ? 'save' : 'block';
    }

    case 'unavailable': {
      return 'saveUnchecked';
    }

    default: {
      return 'save';
    }
  }
}
